/**
 * ContactOneOffActionSectionPure
 *
 * Pure presentational component for one-off actions (action tab).
 * Receives all data and callbacks via props, no server dependencies.
 *
 * Step Editor Approach:
 * - When `stepEditorData` is provided, renders `CadenceStepSectionPure` directly
 */
import type { ComponentType, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';

import type {
  AiChoice,
  EditorDocument,
  EmailOption,
  MessageAttachmentState,
  ReminderAction,
  StepChanges,
  StepData,
  StepListItem,
} from '../CadenceStep';
import type { VoiceRecorderProps } from '../VoiceRecorder';
import type { PreviousEmailThread } from './previousEmailThread';
import type {
  AudioDropFileData,
  ContactDossier,
  ContactPrimaryFields,
  GlobalReminder,
  GlobalReminderWithUser,
  ReminderActionData,
  TeamUserOption,
  VoiceBackground,
} from './types';
import type { VoiceScriptRequest } from './VoiceNoteScriptPicker';
import { CadenceStepSectionPure } from '../CadenceStep';
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import FilterSelect from '../Input2/FilterSelect';
import Textarea from '../Input2/Textarea';
import { Scrollable } from '../Scrollable';
import { Arrows, General, Users } from '../StrokeIcons';
import { ActionCardPure } from './ActionCardPure';
import { DossierSummaryPure } from './DossierSummaryPure';
import { PendingStepBannerPure } from './PendingStepBannerPure';
import { ReminderActionCardPure } from './ReminderActionCardPure';
import { ScheduleStatusBannerPure } from './ScheduleStatusBannerPure';
import { UnipileConnectBannerPure } from './UnipileConnectBannerPure';
import { VideoNoteActionCardPure } from './VideoNoteActionCardPure';
import { VoiceNoteActionCardPure } from './VoiceNoteActionCardPure';

// ============================================================================
// Types
// ============================================================================

export type StepType = 'message' | 'connect' | 'email' | 'inmail' | 'follow' | 'reminder' | 'voice_note' | 'video_note';

export type ActionStep = {
  id: string;
  type: StepType;
  subject: string | null;
  body2: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  bcc: string | null;
  useEmailSignature?: boolean;
  /** Email only: send as a reply in the contact's last email thread instead of starting a new one. */
  replyToPrevious?: boolean;
};

export type PendingStep = {
  id: string;
  bodyPreview: string | null;
  type: string;
  status: string;
  scheduledFor: string | null;
};

/**
 * When a queued step will actually go out. `pendingStep.scheduledFor` is the step's own
 * `next_run_time`, which for a just-approved step is "now" — but the worker only sends
 * inside the rep's LinkedIn schedule, so outside the window the honest answer is the
 * next window start. Shared by the quick-actions banner and the history tab.
 */
export function pendingStepSendsAt(
  pendingStep: Pick<PendingStep, 'scheduledFor'>,
  scheduleStatus: Pick<ScheduleStatus, 'isWithinSchedule' | 'nextWindowStartMs'> | null | undefined,
): string | null {
  if (
    scheduleStatus != null &&
    !scheduleStatus.isWithinSchedule &&
    scheduleStatus.nextWindowStartMs != null &&
    scheduleStatus.nextWindowStartMs > Date.now()
  ) {
    return new Date(scheduleStatus.nextWindowStartMs).toISOString();
  }
  return pendingStep.scheduledFor;
}

export type ScheduleStatus = {
  isConfigured: boolean;
  isWithinSchedule: boolean;
  nextWindowStartMs: number | null;
  nextWindowStartFormatted: string | null;
  settingsUrl: string;
};

export type ActionStepConfig = {
  value: StepType;
  labelFull: ReactNode;
  labelFullIfResponse?: ReactNode;
  error?: string;
};

export type SendMode = 'now' | 'scheduled';

/**
 * User team data required by this component
 */
export type ActionSectionUserTeam = {
  userId: string;
  teamId: string;
  unipileAccountId?: string | null;
  /** The rep's ElevenLabs voice, set by a superadmin — the gate for the voice note card. */
  elevenLabsVoiceId?: string | null;
  isSuperAdmin?: boolean;
  mailType?: string | null;
  sendMessageType?: string | null;
  scheduleIsWithinSchedule?: boolean;
  scheduleNextWindowStartFormatted?: string | null;
  // Additional fields for step editor
  isPremium: boolean;
  useEmailSignatureByDefault?: boolean;
  useContactEmailAsBcc?: boolean;
};

/**
 * Step editor data - all the data needed to render CadenceStepSectionPure directly
 */
export type StepEditorData = {
  // Server data
  documents: EditorDocument[];
  fromEmailOptions: EmailOption[];
  toEmailOptions: EmailOption[];
  /**
   * The email thread this user can continue with the contact, or null when there is none. Non-null
   * gates the composer's "Reply in thread" toggle; its `subject` fills the thread banner. Derive it
   * with `getPreviousEmailThread`.
   */
  previousEmailThread: PreviousEmailThread | null;
  /**
   * Whether that email was also the last step run with this contact — when it was, the quick-action
   * composer opens with "Reply in thread" already on. Derive it with `wasLastStepEmail`.
   */
  lastStepWasEmail: boolean;
  reminderActions: ReminderAction[];
  stepsList: StepListItem[];

  // AI state (managed by parent)
  aiChoices: AiChoice[];
  aiIndex: number;
  aiCommand: string;

  // Loading states
  isAiGenerating: boolean;
  isLoadingReminderActions: boolean;
  isCreatingReminderAction: boolean;
  isSavingSnippet: boolean;
};

/**
 * Step editor callbacks - mutations handled by parent
 */
export type StepEditorCallbacks = {
  onGenerateAi: (empty?: boolean) => Promise<void>;
  onAiCommandChange: (command: string) => void;
  onAiIndexChange: (index: number) => void;
  onCreateReminderAction: (name: string) => Promise<void>;
  onSaveSnippet?: () => Promise<void>;
  onToast: (message: string, type: 'error' | 'success') => void;
  onStepChange: (changes: StepChanges) => void;
};

/**
 * Reminder card data - data needed to render ReminderActionCardPure directly
 */
export type ReminderCardData = {
  // Flattened from contactUser - only field actually used
  currentUserId: string;
  cadenceReminders: Array<{
    globalReminder: GlobalReminder & {
      planRunAt?: Date | null;
      body?: string | null;
      cadenceStep: NonNullable<GlobalReminder['cadenceStep']> & { type: string };
    };
  }>;
  nonCadenceReminders: GlobalReminderWithUser[];
  defaultReminderAction?: ReminderActionData;
  reminderOptions: { value: string; label: string }[];
  teamUsers: TeamUserOption[];
  isLoading: boolean;
  deletingIds: Set<string>;
  createPending: boolean;
  updatePending: boolean;
  changeOwnerPending: boolean;
  /** Whether a reminder type is currently being created from the type dropdown. */
  creatingReminderAction?: boolean;
};

/**
 * Reminder card callbacks
 */
export type ReminderCardCallbacks = {
  onCreateReminder: (planRunAt: number, body?: string) => Promise<void>;
  onUpdateReminder: (id: string, planRunAt: number, body?: string) => Promise<void>;
  onDeleteReminder: (id: string) => Promise<void>;
  onChangeOwner: (reminderId: string, newUserId: string) => Promise<void>;
  onNavigateToInteract: () => void;
  /** Persist a new reusable reminder type from the type dropdown, then select it. */
  onCreateReminderAction?: (name: string) => Promise<void>;
};

/**
 * Voice note card data - data needed to render VoiceNoteActionCardPure directly
 */
export type VoiceNoteCardData = {
  audioDropFiles: AudioDropFileData[];
  audioDropFilesIsLoading: boolean;
  /** The rep's remembered background under the AI voice (Off / Office / Office + phone). */
  voiceBackground?: VoiceBackground;
};

/**
 * Voice note card callbacks
 */
export type VoiceNoteCardCallbacks = {
  onGenerateVoiceNote?: (
    text: string,
    voiceDropId?: string,
    background?: VoiceBackground,
  ) => Promise<{ stepId: string; audioUrl: string; audioId: string } | undefined>;
  /** Background picked in the card; see VoiceNoteActionCardPure. Absent → no Background control. */
  onVoiceBackgroundChange?: (background: VoiceBackground) => void;
  onApproveVoiceNote?: (stepId: string) => Promise<void>;
  onAudioGenerated?: (publicUrl: string, audioId: string) => void;
  /** Write up to three spoken takes for the voice-note script picker (angle and/or steer). */
  onGenerateVoiceScripts?: (request: VoiceScriptRequest) => Promise<string[]>;
  /** Called when user records a voice note. May be async; the card awaits it and
      closes itself once it resolves. */
  onRecordingComplete?: (audioBlob: Blob, durationMs: number) => void | Promise<void>;
  /** Whether a recording is currently being processed */
  isUploadingRecording?: boolean;
  /** Callback to trigger record UI */
  onRecordTrigger?: () => void;
};

/**
 * Video note card callbacks — pass only when the team has `videoNotesEnabled`; absent hides the card.
 */
export type VideoNoteCardCallbacks = {
  /** Write ONE script for the optional on-camera teleprompter. */
  onGenerateVideoScript?: () => Promise<string>;
  /** Upload + send the recorded take. Resolve to close the card; reject to keep the take for a retry. */
  onSendVideo: (video: Blob, durationMs: number, script: string) => Promise<void>;
};

export type ContactOneOffActionSectionPureProps = {
  // Core data - flattened from Contact and ContactUser
  contactId: string;
  contactPrimaryFields?: ContactPrimaryFields;
  liConnectedAt: Date | null;
  emailCredentialId: string | null;
  userTeam: ActionSectionUserTeam;
  cadenceId?: string | null;

  // Query data
  pendingStep?: PendingStep | null;
  scheduleStatus?: ScheduleStatus | null;

  // Step configuration
  linkedinSteps: ActionStepConfig[];
  otherSteps: ActionStepConfig[];

  // Current action state
  currentAction?: string;
  onActionChange: (action: string | undefined) => void;

  // Callbacks for actions
  onSubmitStep: (step: ActionStep, sendMode: SendMode) => Promise<void>;
  onAddToTakeAction: (onlyNewPeople: boolean) => Promise<void>;
  /** Persisted "Only new people" preference for the Add-to-Take-Action picker. */
  addToTakeActionOnlyNewPeople: boolean;
  /** Called when the user flips the picker; adapter persists to user_team_data. */
  onChangeAddToTakeActionOnlyNewPeople: (onlyNewPeople: boolean) => void;
  onDeletePendingStep?: () => void;
  /** "Send now" on the queued step (approved steps only). Absent → no button. */
  onSendNowPendingStep?: () => void;
  isSendingNowPendingStep?: boolean;
  isDeletingPendingStep?: boolean;

  // Unipile connect callback
  onUnipileConnect?: () => void;
  isUnipileConnecting?: boolean;

  // Loading states
  isAddingToTakeAction?: boolean;
  isSubmitting?: boolean;

  // At variables for template conversion (passed to step editor)
  atVariables?: Record<string, string>;

  // Initial step data (from cached form)
  getInitialStep?: (type: StepType) => ActionStep;

  /**
   * The as-you-type template picker. Injected rather than built in so the
   * dashboard and the extension side panel can each supply their own data
   * source while rendering the identical picker (see TemplatePickerPure).
   */
  renderSuggestTemplates?: (props: {
    value: string;
    atVariables: Record<string, string>;
    documents: EditorDocument[];
    onClick: (body2: string, subject?: string) => void;
    forceShow: boolean;
  }) => ReactNode;

  // Step editor data (pure approach - when provided, renders CadenceStepSectionPure directly)
  stepEditorData?: StepEditorData;
  stepEditorCallbacks?: StepEditorCallbacks;

  // Auto-generate AI flag (passed to step editor)
  autoGenerateAi?: boolean;
  onAutoGenerateAiChange?: (value: boolean) => void;

  // Config for step editor
  allowEditStepType?: boolean;
  allowSaveAsTemplate?: boolean;
  allowEmailDetails?: boolean;
  disableLikeRecentPosts?: boolean;

  // Action card props
  /** Whether the current platform is macOS-like (for facetime button) */
  isMacLike?: boolean;
  /** Phone number for call step type */
  tel?: string | null;
  /** Callback when manual call is recorded (for tel: links) */
  onRecordManualCall?: () => void;
  /** Injected in-app click-to-call trigger; attaches to the first action card only. */
  clickToCall?: ReactNode;
  /**
   * Injected "Calling from … / Change" caption for the contact's pinned from-number — rendered above
   * the action rows, so the rep can pin the number BEFORE dialing (see ContactDialNumberPure).
   * Omitted when voice calling is off.
   */
  dialFromNumber?: ReactNode;
  /**
   * Injected dialer factory for a `call`-type cadence reminder. Returns the surface's
   * click-to-call button wired to the given step id (see ReminderActionCardPure). The
   * `clickToCall` slot above only reaches action cards, not the reminder card, so this is a
   * separate seam.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => ReactNode;
  /** Completes a `call`-type cadence reminder the rep handled outside the dialer (see ReminderActionCardPure). */
  onMarkCallStepDone?: (args: {
    cadenceStepId: string;
    outcome: string;
    notes?: string;
    callbackAt?: number;
  }) => Promise<void> | void;
  /** Injected dialer factory for a standalone `call`-kind reminder (see ReminderActionCardPure). */
  renderReminderCallButton?: (args: { globalReminderId: string }) => ReactNode;
  /**
   * Injected "Schedule a call" affordance — rendered as its own full-width row directly below the
   * reminder card. Schedules a standalone future call on the contact (no campaign). Omitted when
   * voice calling is disabled.
   */
  scheduleCall?: ReactNode;

  // Reminder card data (pure approach)
  reminderCardData?: ReminderCardData;
  reminderCardCallbacks?: ReminderCardCallbacks;

  // Voice note card data (pure approach)
  voiceNoteCardData?: VoiceNoteCardData;
  /** Recorded video notes (team flag `videoNotesEnabled`). Absent → no video card. */
  videoNoteCardCallbacks?: VideoNoteCardCallbacks;
  voiceNoteCardCallbacks?: VoiceNoteCardCallbacks;
  /** Optional replacement for the built-in `VoiceRecorder` inside the voice-note card. */
  voiceRecorderComponent?: ComponentType<VoiceRecorderProps>;

  /**
   * File attachment for LinkedIn message/inmail sends. When provided, an attach
   * button appears in the message composer. `filename != null` means a file is
   * staged — while staged, sends are forced to "now" (attachments can't be
   * scheduled). Omit entirely on surfaces that don't support attachments.
   */
  messageAttachment?: MessageAttachmentState;

  // Dossier summary (research) — the condensed dossier surfaced inline in the
  // quick-actions list, above the superadmin "Generate sample message" preview.
  // Same data the Dossier tab renders; shown only when the team has the dossier
  // feature enabled (mirrors the Dossier tab's flag gate).
  /** Whether to surface the dossier summary at all (feature gate). */
  showDossier?: boolean;
  /** The contact's research dossier. `null` → empty state (with optional generate button). */
  dossier?: ContactDossier | null;
  /** True while the research worker is still building the dossier. */
  dossierGenerating?: boolean;
  /** Manually kick off (or retry) dossier generation. `undefined` → no button shown. */
  onGenerateDossier?: () => void;
  /** True while the manual dossier-generate mutation is in flight (button spinner). */
  isGeneratingDossier?: boolean;

  // Superadmin AI message preview (visible only when userTeam.isSuperAdmin)
  superAdminAiPreview?: {
    onGenerate: () => void;
    isGenerating: boolean;
    prompt: string | null;
    message: string | null;
    error?: string | null;
    instructions: string;
    onChangeInstructions: (value: string) => void;
    shouldSend?: { shouldSend: boolean; reasoning: string } | null;
  };

  /**
   * Render prop for step editor (testing/flexibility).
   * When provided, this is used instead of CadenceStepSectionPure.
   */
  renderStepEditor?: (props: {
    step: ActionStep;
    sendMode: SendMode;
    onSendModeChange: (mode: SendMode) => void;
    showSendModeDropdown: boolean;
    onSubmit: (step: ActionStep) => Promise<void>;
    onCancel: () => void;
    initiallyAnimate: boolean;
    submitText: string;
  }) => ReactNode;
};

// ============================================================================
// Styled Components
// ============================================================================

const B = ({ children }: { children: ReactNode }) => (
  <span className="font-medium text-zumthor-700 underline decoration-zumthor-300/50 underline-offset-2 group-hover/btn:text-white group-hover/btn:decoration-white/60 dark:text-zumthor-300 dark:decoration-zumthor-500/50">
    {children}
  </span>
);

export const defaultFormattedLabels: Record<string, ReactNode> = {
  connect: (
    <span>
      Send Linkedin <B>Connection</B>
    </span>
  ),
  message: (
    <span>
      Send Linkedin <B>Direct Message</B>
    </span>
  ),
  inmail: (
    <span>
      Send Linkedin <B>InMail</B>
    </span>
  ),
  email: (
    <span>
      Send <B>Email</B>
    </span>
  ),
};

// ============================================================================
// Component
// ============================================================================

export function ContactOneOffActionSectionPure({
  contactId,
  contactPrimaryFields,
  liConnectedAt,
  emailCredentialId,
  userTeam,
  cadenceId: _cadenceId,
  pendingStep,
  scheduleStatus,
  linkedinSteps,
  otherSteps,
  currentAction,
  onActionChange,
  onSubmitStep,
  onAddToTakeAction,
  addToTakeActionOnlyNewPeople,
  onChangeAddToTakeActionOnlyNewPeople,
  onDeletePendingStep,
  isDeletingPendingStep,
  onSendNowPendingStep,
  isSendingNowPendingStep,
  onUnipileConnect,
  isUnipileConnecting,
  isAddingToTakeAction,
  isSubmitting,
  // Reminder card (pure approach)
  reminderCardData,
  reminderCardCallbacks,
  renderCadenceCallButton,
  onMarkCallStepDone,
  renderReminderCallButton,
  scheduleCall,
  // Voice note card (pure approach)
  voiceNoteCardData,
  voiceNoteCardCallbacks,
  videoNoteCardCallbacks,
  voiceRecorderComponent,
  messageAttachment,
  getInitialStep,
  atVariables = {},
  renderSuggestTemplates,
  stepEditorData,
  stepEditorCallbacks,
  autoGenerateAi: autoGenerateAiProp,
  onAutoGenerateAiChange,
  allowEditStepType = true,
  allowSaveAsTemplate = true,
  allowEmailDetails = true,
  disableLikeRecentPosts = false,
  isMacLike,
  tel,
  onRecordManualCall,
  clickToCall,
  dialFromNumber,
  renderStepEditor,
  superAdminAiPreview,
  showDossier,
  dossier,
  dossierGenerating,
  onGenerateDossier,
  isGeneratingDossier,
}: ContactOneOffActionSectionPureProps) {
  // Determine disabled states based on contact data
  const connectedAt = liConnectedAt;
  const professionalEmail = contactPrimaryFields?.professionalEmail?.data;
  const hasEmail = professionalEmail != null && professionalEmail !== '';

  const disabledState: Partial<Record<StepType, boolean>> = {
    email: !hasEmail,
    follow: false,
    connect: connectedAt != null,
    inmail: connectedAt != null,
    message: connectedAt == null,
    reminder: true, // Custom UI for reminders
    voice_note: false,
    video_note: false,
  };

  const enabledLinkedinSteps = linkedinSteps.filter((s) => !disabledState[s.value]);
  const disabledLinkedinSteps = linkedinSteps.filter((s) => disabledState[s.value]);
  const enabledOtherSteps = otherSteps.filter((s) => !disabledState[s.value]);
  const disabledOtherSteps = otherSteps.filter((s) => disabledState[s.value]);
  const allDisabledSteps = [...disabledLinkedinSteps, ...disabledOtherSteps];

  // Local state
  const [showHidden, setShowHidden] = useState(false);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [showAiPreview, setShowAiPreview] = useState(false); // collapsed + faded by default
  const [editingStep, setEditingStep] = useState<ActionStep | undefined>(() => {
    if (currentAction && getInitialStep) {
      return getInitialStep(currentAction as StepType);
    }
    return undefined;
  });
  const [localAutoGenerateAi, setLocalAutoGenerateAi] = useState(false);
  const autoGenerateAi = autoGenerateAiProp ?? localAutoGenerateAi;
  const setAutoGenerateAi = useCallback(
    (value: boolean) => {
      if (onAutoGenerateAiChange) {
        onAutoGenerateAiChange(value);
      } else {
        setLocalAutoGenerateAi(value);
      }
    },
    [onAutoGenerateAiChange],
  );
  const [wasClosed, setWasClosed] = useState(false);
  const [sendMode, setSendMode] = useState<SendMode>(userTeam.scheduleIsWithinSchedule ? 'now' : 'scheduled');

  // A staged attachment forces immediate send — attachments can't be scheduled.
  const hasMessageAttachment = messageAttachment?.filename != null;

  // Track if step was open initially (for animation)
  const hadStepInitiallyRef = useRef<boolean | null>(null);
  if (hadStepInitiallyRef.current === null) {
    hadStepInitiallyRef.current = editingStep !== undefined;
  }

  // Helpers
  const hasUnipile = userTeam.unipileAccountId != null;
  const showUnipileConnectPrompt = !hasUnipile && enabledLinkedinSteps.length > 0;

  const handleClickAction = (stepType: StepType) => {
    // Attachments only apply to a LinkedIn message/inmail — drop a staged file when
    // switching to any other action so it can't ride along with the wrong send.
    if (stepType !== 'message' && stepType !== 'inmail') {
      messageAttachment?.onRemove();
    }
    if (stepType === 'message' || stepType === 'connect' || stepType === 'email' || stepType === 'inmail') {
      setEditingStep((s) => {
        const initial = getInitialStep?.(stepType) ?? createDefaultStep(stepType, userTeam.userId, contactId);
        if (s == null) return initial;

        if (stepType === 'email') {
          return { ...s, type: stepType };
        } else {
          return { ...s, type: stepType, from: null, to: null, cc: null, bcc: null };
        }
      });
    }
    onActionChange(stepType);
  };

  const handleCancel = () => {
    setWasClosed(true);
    setEditingStep(undefined);
    setAutoGenerateAi(false);
    messageAttachment?.onRemove();
    onActionChange(undefined);
  };

  const handleSubmit = async (step: ActionStep) => {
    // A staged attachment can't be scheduled — force immediate send.
    await onSubmitStep(step, hasMessageAttachment ? 'now' : sendMode);
    handleCancel();
  };

  const preventOpenForm = editingStep?.type ? disabledState[editingStep.type] : false;

  // Determine submit button text
  const getSubmitText = () => {
    if (!editingStep) return 'Send';
    if (editingStep.type === 'email' && userTeam.mailType === 'gmail-api') {
      return 'Send';
    }
    return 'Schedule to Send';
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      {scheduleStatus && <ScheduleStatusBannerPure scheduleStatus={scheduleStatus} />}

      <Scrollable
        className="p-4"
        innerClassName="flex flex-col gap-1.5 [&_button]:text-sm [&_button]:font-normal [&_button]:tracking-tight">
        {showUnipileConnectPrompt && onUnipileConnect && (
          <UnipileConnectBannerPure onConnect={onUnipileConnect} isConnecting={isUnipileConnecting} />
        )}

        {pendingStep && onDeletePendingStep && (
          <PendingStepBannerPure
            stepId={pendingStep.id}
            bodyPreview={pendingStep.bodyPreview}
            type={pendingStep.type}
            scheduledFor={pendingStepSendsAt(pendingStep, scheduleStatus)}
            status={pendingStep.status}
            onDelete={onDeletePendingStep}
            isDeleting={isDeletingPendingStep}
            onSendNow={onSendNowPendingStep}
            isSendingNow={isSendingNowPendingStep}
          />
        )}

        {/* Which number the next call goes out from, with the pre-call "Change" pick. Sits above the
            action rows: it describes the call the rep is about to place, so it reads as a heading for
            them rather than a footnote to whichever row happened to render last. */}
        {dialFromNumber}

        {enabledLinkedinSteps.map((config, index) => (
          <ActionCardPure
            key={config.value}
            label={defaultFormattedLabels[config.value] ?? config.labelFull}
            iconType={config.value}
            onClick={() => handleClickAction(config.value)}
            // Phone/SMS buttons attach to the first action card only — otherwise
            // every enabled LinkedIn step renders its own duplicate pair.
            tel={index === 0 ? (tel ?? undefined) : undefined}
            showFacetimeButton={isMacLike}
            onRecordManualCall={onRecordManualCall}
            clickToCall={index === 0 ? clickToCall : undefined}
          />
        ))}

        {(hasUnipile || userTeam.isSuperAdmin) &&
          userTeam.elevenLabsVoiceId != null &&
          voiceNoteCardData &&
          voiceNoteCardCallbacks && (
            <VoiceNoteActionCardPure
              audioDropFiles={voiceNoteCardData.audioDropFiles}
              audioDropFilesIsLoading={voiceNoteCardData.audioDropFilesIsLoading}
              onGenerateVoiceNote={voiceNoteCardCallbacks.onGenerateVoiceNote}
              onApproveVoiceNote={voiceNoteCardCallbacks.onApproveVoiceNote}
              onAudioGenerated={voiceNoteCardCallbacks.onAudioGenerated}
              onRecordTrigger={voiceNoteCardCallbacks.onRecordTrigger}
              onRecordingComplete={voiceNoteCardCallbacks.onRecordingComplete}
              isUploadingRecording={voiceNoteCardCallbacks.isUploadingRecording}
              onGenerateVoiceScripts={voiceNoteCardCallbacks.onGenerateVoiceScripts}
              voiceAngles={dossier?.talkingPoints ?? []}
              voiceRecorderComponent={voiceRecorderComponent}
              voiceBackground={voiceNoteCardData.voiceBackground}
              onVoiceBackgroundChange={voiceNoteCardCallbacks.onVoiceBackgroundChange}
            />
          )}

        {(hasUnipile || userTeam.isSuperAdmin) && videoNoteCardCallbacks && (
          <VideoNoteActionCardPure
            onGenerateVideoScript={videoNoteCardCallbacks.onGenerateVideoScript}
            onSendVideo={videoNoteCardCallbacks.onSendVideo}
          />
        )}

        {enabledOtherSteps.map((config) => (
          <ActionCardPure
            key={config.value}
            label={defaultFormattedLabels[config.value] ?? config.labelFull}
            iconType={config.value}
            onClick={() => handleClickAction(config.value)}
          />
        ))}

        {reminderCardData && reminderCardCallbacks && (
          <ReminderActionCardPure
            currentUserId={reminderCardData.currentUserId}
            cadenceReminders={reminderCardData.cadenceReminders}
            nonCadenceReminders={reminderCardData.nonCadenceReminders}
            defaultReminderAction={reminderCardData.defaultReminderAction}
            reminderOptions={reminderCardData.reminderOptions}
            teamUsers={reminderCardData.teamUsers}
            isLoading={reminderCardData.isLoading}
            deletingIds={reminderCardData.deletingIds}
            createPending={reminderCardData.createPending}
            updatePending={reminderCardData.updatePending}
            changeOwnerPending={reminderCardData.changeOwnerPending}
            onCreateReminder={reminderCardCallbacks.onCreateReminder}
            onUpdateReminder={reminderCardCallbacks.onUpdateReminder}
            onDeleteReminder={reminderCardCallbacks.onDeleteReminder}
            onChangeOwner={reminderCardCallbacks.onChangeOwner}
            onNavigateToInteract={reminderCardCallbacks.onNavigateToInteract}
            onCreateReminderAction={reminderCardCallbacks.onCreateReminderAction}
            creatingReminderAction={reminderCardData.creatingReminderAction}
            renderCadenceCallButton={renderCadenceCallButton}
            onMarkCallStepDone={onMarkCallStepDone}
            renderReminderCallButton={renderReminderCallButton}
          />
        )}

        {/* Standalone "Schedule a call" row — sits directly below the reminder card. */}
        {scheduleCall}

        <ButtonGroup color="blue" tone="soft" className="w-full">
          <FilterSelect
            color="blue"
            tone="soft"
            value={addToTakeActionOnlyNewPeople ? 'only_new' : 'everyone'}
            options={[
              {
                value: 'only_new',
                label: 'Only new people',
                description: 'Skip this contact if you have ever added them to Take Action',
                icon: <Users.UserPlus01 />,
              },
              {
                value: 'everyone',
                label: 'Everyone',
                description: 'Add this contact even if you previously added them',
                icon: <Users.Users01 />,
              },
            ]}
            onChange={(v) => onChangeAddToTakeActionOnlyNewPeople(v === 'only_new')}
            tooltip={
              addToTakeActionOnlyNewPeople
                ? 'Only add if you have never added this contact to Take Action'
                : 'Add even if you previously added this contact to Take Action'
            }
            filterable={false}
            showText={false}
            showChevron={false}
            autoWidth
          />
          <Button
            color="blue"
            tone="soft"
            icon={<General.Zap />}
            onClick={() => onAddToTakeAction(addToTakeActionOnlyNewPeople)}
            disabled={isAddingToTakeAction}
            className="flex-1">
            <span>+ Take Action</span>
          </Button>
        </ButtonGroup>

        {showDossier && (
          <DossierSummaryPure
            dossier={dossier ?? null}
            generating={dossierGenerating}
            onGenerateDossier={onGenerateDossier}
            isGeneratingDossier={isGeneratingDossier}
            className="mt-2 rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-800"
          />
        )}

        {userTeam.isSuperAdmin && superAdminAiPreview && (
          <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-4 dark:border-gray-800">
            <Button
              text
              size="sm"
              onClick={() => setShowAiPreview((v) => !v)}
              className="group/ai-preview w-full"
              rIcon={
                <Arrows.ChevronDown
                  className={cn(
                    'size-3 text-gray-500 opacity-50 transition-all group-hover/ai-preview:opacity-100 dark:text-gray-400',
                    !showAiPreview && '-rotate-90',
                  )}
                />
              }>
              <span
                className={cn(
                  'text-sm font-semibold text-gray-700 transition-opacity dark:text-gray-200',
                  !showAiPreview && 'opacity-50 group-hover/ai-preview:opacity-100',
                )}>
                Generate sample message
              </span>
            </Button>
            {showAiPreview && (
              <>
                <div className="flex flex-col gap-1 rounded-sm border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
                  <span>🔒 Contact (auto-filled from this contact)</span>
                  <span>🔒 Sender (auto-filled from your profile)</span>
                  <span>Tone — set in Assist settings</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Take Action message instructions
                  </span>
                  <Textarea
                    value={superAdminAiPreview.instructions}
                    onChange={(e) => superAdminAiPreview.onChangeInstructions(e.target.value)}
                    placeholder="Leave blank for the default: one question that ties a real detail about them to your work (Niche, Focus). No pitch, no meeting ask."
                    rows={4}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    text
                    size="xs"
                    onClick={() => setShowFullPrompt((v) => !v)}
                    className="group/full-prompt w-full"
                    rIcon={
                      <Arrows.ChevronDown
                        className={cn(
                          'size-3 text-gray-500 opacity-50 transition-all group-hover/full-prompt:opacity-100 dark:text-gray-400',
                          !showFullPrompt && '-rotate-90',
                        )}
                      />
                    }>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Full prompt
                    </span>
                  </Button>
                  {showFullPrompt && (
                    <pre className="whitespace-pre-wrap rounded-sm border border-gray-200 bg-white p-2 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                      {superAdminAiPreview.prompt ?? '(Click Generate to build the prompt and run it)'}
                    </pre>
                  )}
                </div>
                <Button
                  size="xs"
                  color="purple"
                  icon={<General.Zap />}
                  onClick={superAdminAiPreview.onGenerate}
                  disabled={superAdminAiPreview.isGenerating}
                  className="w-full">
                  {superAdminAiPreview.isGenerating ? 'Generating…' : 'Generate'}
                </Button>
                {superAdminAiPreview.message && (
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Generated message
                    </span>
                    <pre className="whitespace-pre-wrap rounded-sm border border-gray-200 bg-white p-2 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                      {superAdminAiPreview.message}
                    </pre>
                    {superAdminAiPreview.shouldSend?.shouldSend === true && (
                      <div className="text-xs text-green-600 dark:text-green-400">
                        {superAdminAiPreview.shouldSend.reasoning || 'Looks good to send.'}
                      </div>
                    )}
                    {superAdminAiPreview.shouldSend?.shouldSend === false && (
                      <div className="text-xs text-red-600 dark:text-red-400">
                        Reconsider: {superAdminAiPreview.shouldSend.reasoning}
                      </div>
                    )}
                  </div>
                )}
                {superAdminAiPreview.error && (
                  <div className="text-xs text-red-600 dark:text-red-400">{superAdminAiPreview.error}</div>
                )}
              </>
            )}
          </div>
        )}

        {showHidden &&
          allDisabledSteps.map((config) => (
            <ActionCardPure
              key={config.value}
              label={defaultFormattedLabels[config.value] ?? config.labelFull}
              iconType={config.value}
              disabled
              error={config.error}
              onClick={() => handleClickAction(config.value)}
            />
          ))}
      </Scrollable>

      {allDisabledSteps.length > 0 && (
        <div className="relative z-0 flex shrink-0 justify-center border-t border-gray-100 bg-white px-4 py-2 dark:border-gray-800 dark:bg-gray-900">
          <button
            onClick={() => setShowHidden((v) => !v)}
            className="text-xs text-gray-400 underline decoration-gray-400/30 hover:text-gray-600 hover:decoration-gray-600/30 dark:hover:text-gray-300 dark:hover:decoration-gray-300/30">
            {showHidden ? 'Hide' : 'Show'} {allDisabledSteps.length} unavailable action
            {allDisabledSteps.length !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Step editor - render prop approach (for testing/flexibility) */}
      {!preventOpenForm &&
        editingStep &&
        renderStepEditor &&
        renderStepEditor({
          step: editingStep,
          sendMode,
          onSendModeChange: setSendMode,
          showSendModeDropdown:
            hasUnipile &&
            (editingStep.type === 'message' || editingStep.type === 'inmail' || editingStep.type === 'connect'),
          onSubmit: handleSubmit,
          onCancel: handleCancel,
          initiallyAnimate: hadStepInitiallyRef.current === true && !wasClosed,
          submitText: getSubmitText(),
        })}

      {/* Step editor - pure approach (renders CadenceStepSectionPure directly) */}
      {!preventOpenForm &&
        editingStep &&
        !renderStepEditor &&
        isStepEditorSupportedType(editingStep.type) &&
        stepEditorData &&
        stepEditorCallbacks && (
          <CadenceStepSectionPure
            renderSuggestTemplates={renderSuggestTemplates}
            // Core data
            userTeam={{
              teamId: userTeam.teamId,
              userId: userTeam.userId,
              isPremium: userTeam.isPremium,
              useEmailSignatureByDefault: userTeam.useEmailSignatureByDefault,
              useContactEmailAsBcc: userTeam.useContactEmailAsBcc,
              unipileAccountId: userTeam.unipileAccountId,
            }}
            contact={{
              id: contactId,
              primaryFields: contactPrimaryFields,
            }}
            contactUser={{
              liConnectedAt,
              emailCredentialId,
            }}
            step={convertActionStepToStepData(editingStep)}
            atVariables={atVariables}
            // Server data
            documents={stepEditorData.documents}
            fromEmailOptions={stepEditorData.fromEmailOptions}
            toEmailOptions={stepEditorData.toEmailOptions}
            previousEmailThread={stepEditorData.previousEmailThread}
            lastStepWasEmail={stepEditorData.lastStepWasEmail}
            // AI state
            aiChoices={stepEditorData.aiChoices}
            aiIndex={stepEditorData.aiIndex}
            aiCommand={stepEditorData.aiCommand}
            aiLoading={stepEditorData.isAiGenerating}
            // Step list
            stepsList={stepEditorData.stepsList}
            // Config
            allowEditStepType={allowEditStepType}
            allowSaveAsTemplate={allowSaveAsTemplate}
            allowEmailDetails={allowEmailDetails}
            disableLikeRecentPosts={disableLikeRecentPosts}
            autoGenerateAi={autoGenerateAi}
            initiallyAnimate={hadStepInitiallyRef.current && !wasClosed}
            // Send mode — a staged attachment forces "now" and hides the schedule option
            sendMode={hasMessageAttachment ? 'now' : sendMode}
            showSendModeDropdown={
              hasUnipile &&
              !hasMessageAttachment &&
              (editingStep.type === 'message' || editingStep.type === 'inmail' || editingStep.type === 'connect')
            }
            onSendModeChange={setSendMode}
            // File attachment (message/inmail only)
            messageAttachment={
              editingStep.type === 'message' || editingStep.type === 'inmail' ? messageAttachment : undefined
            }
            // Texts
            texts={{ submit: hasMessageAttachment ? 'Send' : getSubmitText() }}
            // Loading states
            isSubmitting={isSubmitting}
            isAiGenerating={stepEditorData.isAiGenerating}
            isSavingSnippet={stepEditorData.isSavingSnippet}
            // Reminder data
            reminderActions={stepEditorData.reminderActions}
            isLoadingReminderActions={stepEditorData.isLoadingReminderActions}
            onCreateReminderAction={stepEditorCallbacks.onCreateReminderAction}
            isCreatingReminderAction={stepEditorData.isCreatingReminderAction}
            // Callbacks
            onSubmit={async (stepData) => {
              await handleSubmit(convertStepDataToActionStep(stepData));
            }}
            onCancel={handleCancel}
            onChange={stepEditorCallbacks.onStepChange}
            onGenerateAi={stepEditorCallbacks.onGenerateAi}
            onAiCommandChange={stepEditorCallbacks.onAiCommandChange}
            onAiIndexChange={stepEditorCallbacks.onAiIndexChange}
            onSaveSnippet={stepEditorCallbacks.onSaveSnippet}
            onToast={stepEditorCallbacks.onToast}
          />
        )}
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

function createDefaultStep(type: StepType, _userId: string, _contactId: string): ActionStep {
  return {
    id: '',
    type,
    subject: type === 'email' || type === 'inmail' ? '' : null,
    body2: '',
    from: null,
    to: null,
    cc: null,
    bcc: null,
  };
}

/**
 * Types supported by CadenceStepSectionPure
 */
const STEP_EDITOR_SUPPORTED_TYPES: StepType[] = ['message', 'connect', 'email', 'inmail', 'reminder'];

/**
 * Check if a step type is supported by CadenceStepSectionPure
 */
function isStepEditorSupportedType(
  type: StepType,
): type is StepType & ('message' | 'connect' | 'email' | 'inmail' | 'reminder') {
  return STEP_EDITOR_SUPPORTED_TYPES.includes(type);
}

/**
 * Convert ActionStep to StepData for CadenceStepSectionPure
 */
function convertActionStepToStepData(step: ActionStep): StepData {
  // Map StepType to CadenceStepType (filtering unsupported types is done at render time)
  const type = step.type as StepData['type'];
  return {
    id: step.id,
    type,
    subject: step.subject,
    body2: step.body2,
    from: step.from,
    to: step.to,
    cc: step.cc,
    bcc: step.bcc,
    useEmailSignature: step.useEmailSignature,
    replyToPrevious: step.replyToPrevious,
  };
}

/**
 * Convert StepData back to ActionStep
 */
function convertStepDataToActionStep(stepData: StepData): ActionStep {
  return {
    id: stepData.id,
    type: stepData.type as StepType,
    subject: stepData.subject,
    body2: stepData.body2,
    from: stepData.from,
    to: stepData.to,
    cc: stepData.cc,
    bcc: stepData.bcc,
    useEmailSignature: stepData.useEmailSignature,
    replyToPrevious: stepData.replyToPrevious,
  };
}
